import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { arquivoService } from "@/services/api";

import { ArquivoDTO } from "@/types/arquivo.type";
import { tarefaService } from "@/services/api/tarefa.service";
import { TipoArquivo } from "@workflow/types";

export function useTarefaArquivos(tarefaId: number) {
  const queryClient = useQueryClient();
  const queryKey = ["tarefa-arquivos", tarefaId];

  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const { data: arquivos = [], isLoading } = useQuery<ArquivoDTO[]>({
    queryKey,
    queryFn: () => tarefaService.getArquivosByTarefaId(tarefaId),
    enabled: !!tarefaId,
    staleTime: 1000 * 60 * 5,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      setUploadProgress(0);
      const uploadResponse = await arquivoService.uploadArquivo(
        TipoArquivo.TAREFA,
        file,
        undefined,
        setUploadProgress,
      );
      return await tarefaService.anexarArquivo(tarefaId, uploadResponse.id);
    },
    onSuccess: () => {
      toast.success("Arquivo anexado com sucesso!");
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (arquivoId: number) => {
      return await tarefaService.removerArquivo(tarefaId, arquivoId);
    },
    onSuccess: () => {
      toast.success("Arquivo removido com sucesso!");

      queryClient.invalidateQueries({ queryKey: ["tarefa-mensagens", tarefaId] });
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  const downloadArquivo = async (arquivoId: number) => {
    try {
      setDownloadingId(arquivoId);
      const url = await tarefaService.downloadArquivo(tarefaId, arquivoId);
      window.open(url, "_blank");
    } catch (error) {
      console.error(error);
      toast.error("Não foi possível fazer o download do arquivo.");
    } finally {
      setDownloadingId(null);
    }
  };

  return {
    arquivos,
    isLoading,
    isUploading: uploadMutation.isPending,
    uploadProgress,
    isDeleting: deleteMutation.isPending,
    uploadArquivo: (file: File) => uploadMutation.mutateAsync(file),
    deleteArquivo: (arquivoId: number) => deleteMutation.mutateAsync(arquivoId),
    downloadArquivo,
    downloadingId,
  };
}
